import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const IncometaxTable = () => {
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [subDropdownOpen, setSubDropdownOpen] = useState(null); // Manage sub-dropdown states
  // Toggle sub-dropdown based on index
  const toggleSubDropdown = (index) => {
    setSubDropdownOpen((prev) => (prev === index ? null : index)); // Open the clicked dropdown and close others
  };

  const [gpfValue, setGpfValue] = useState(""); // State to track GPF selection
  const handleGpfChange = (e) => {
    setGpfValue(e.target.value); // Update the GPF value based on selection
  };

  const months = [
    "March",
    "April",
    "May",
    "June",
    "July",
    "Aug",
    "Sept",
    "Oct",
    "Nov",
    "Dec",
    "Jan",
    "Feb",
    "Other",
  ];

  const columns = [
    "Basic",
    "DA",
    "HRA",
    "TA",
    "Fix TA",
    "WA",
    "CLA",
    "NPS",
    "BasicArrs",
    "DAArrs",
    "TotalPay",
    "GPF",
    "GPF INST",
    "NPS",
    "DCPS",
    "PT",
    "GIS",
    "LIC",
    "I.Tax",
  ];

  const [monthValues, setMonthValues] = useState(
    months.reduce((acc, month) => {
      acc[month] = columns.reduce((colAcc, col) => {
        colAcc[col] = 0;
        return colAcc;
      }, {});
      return acc;
    }, {})
  );

  const [selectedMonths, setSelectedMonths] = useState(
    months.reduce((acc, month) => {
      acc[month] = false;
      return acc;
    }, {})
  );

  const [calculatedValues, setCalculatedValues] = useState({
    grossSalary: 0,
    otherIncome: 0,
    totalIncome: 0,
    standardDeduction: 50000,
    ptax: 0,
    homeLoanInterest: 0,
    hraExemption: 0,
    gpf: 0,
    dcps: 0,
    gis: 0,
    lic: 0,
    tutionFees: 0,
    nsc: 0,
    pliRd: 0,
    homeLoanPrincipal: 0,
    mutualFund: 0,
    totalDeduction80C: 0,
    ccd1b: 0,
    ccd2: 0,
    mediclaim: 0,
    dependentHandicapped: 0,
    educationLoanInterest: 0,
    electricVehicleLoan: 0,
    donation: 0,
    selfHandicapped: 0,
    totalDeductionVIA: 0,
  });


  const handleMonthSelect = (month) => {
    setSelectedMonths((prevSelectedMonths) => {
      const isSelecting = !prevSelectedMonths[month];

      // Find the last selected month (the last one that was true)
      const previouslySelectedMonths = Object.keys(prevSelectedMonths).filter(
        (m) => prevSelectedMonths[m]
      );
      const lastSelectedMonth =
        previouslySelectedMonths.length > 0
          ? previouslySelectedMonths[previouslySelectedMonths.length - 1]
          : null;

      setMonthValues((prevValues) => {
        const updatedValues = { ...prevValues };

        if (isSelecting && lastSelectedMonth !== null && month !== lastSelectedMonth) {
          // Copy values from the last selected month
          updatedValues[month] = { ...prevValues[lastSelectedMonth] };
        } else if (!isSelecting) {
          // Reset values to zero
          updatedValues[month] = columns.reduce((acc, col) => {
            acc[col] = 0;
            return acc;
          }, {});
        }

        return updatedValues;
      });

      return {
        ...prevSelectedMonths,
        [month]: isSelecting,
      };
    });
  };

  const handleInputChange = (month, column, value) => {
    setMonthValues((prevValues) => {
      const updatedValues = {
        ...prevValues,
        [month]: {
          ...prevValues[month],
          [column]: value,
        },
      };

      const totalPay = [
        "Basic",
        "DA",
        "HRA",
        "TA",
        "Fix TA",
        "WA",
        "CLA",
        "NPS",
        "BasicArrs",
        "DAArrs",
      ].reduce((total, col) => {
        return total + (parseFloat(updatedValues[month][col]) || 0);
      }, 0);

      // Round the totalPay and store it with two decimal places
      updatedValues[month]["TotalPay"] = Math.round(totalPay * 100) / 100; // Rounds to nearest two decimal places

      return updatedValues;
    });
  };

  const calculateTotals = () => {
    return columns.map((column) =>
      months.reduce((total, month) => {
        const value = parseFloat(monthValues[month]?.[column]) || 0;
        return total + value;
      }, 0)
    );
  };

  useEffect(() => {
    const totals = calculateTotals();

    const grossSalary = totals[columns.indexOf("TotalPay")] || 0;
    const ptax = totals[columns.indexOf("PT")] || 0;
    const gpf = totals[columns.indexOf("GPF")] || 0;
    const dcps = totals[columns.indexOf("DCPS")] || 0;
    const gis = totals[columns.indexOf("GIS")] || 0;
    const lic = totals[columns.indexOf("LIC")] || 0;
    const nps = totals[columns.indexOf("NPS")] || 0;
    const itax = totals[columns.indexOf("I.Tax")] || 0; // Get the total I.Tax


    setCalculatedValues((prev) => ({
      ...prev,
      grossSalary,
      ptax,
      gpf,
      dcps,
      gis,
      lic,
      itax, // Add this line to store the total I.Tax
      totalIncome: grossSalary + prev.otherIncome,
      totalDeduction80C:
        gpf +
        dcps +
        gis +
        lic +
        prev.tutionFees +
        prev.nsc +
        prev.pliRd +
        prev.homeLoanPrincipal +
        prev.mutualFund,
      totalDeductionVIA:
        prev.ccd1b +
        prev.ccd2 +
        prev.mediclaim +
        prev.dependentHandicapped +
        prev.educationLoanInterest +
        prev.electricVehicleLoan +
        prev.donation +
        prev.selfHandicapped,
    }));
  }, [monthValues, columns]);

  const handleCalculatedValueChange = (field, value) => {
    setCalculatedValues((prev) => {
      const updatedValues = { ...prev, [field]: parseFloat(value) || 0 };

      updatedValues.totalIncome =
        updatedValues.grossSalary + updatedValues.otherIncome + updatedValues.homeLoanInterest + updatedValues.hraExemption;

      const totalDeduction80CWithoutLimit =
        updatedValues.gpf +
        updatedValues.dcps +
        updatedValues.gis +
        updatedValues.lic +
        updatedValues.tutionFees +
        updatedValues.nsc +
        updatedValues.pliRd +
        updatedValues.homeLoanPrincipal +
        updatedValues.mutualFund;

      updatedValues.totalDeduction80CWithoutLimit = totalDeduction80CWithoutLimit;
      updatedValues.totalDeduction80C = Math.min(totalDeduction80CWithoutLimit, 150000);

      return updatedValues;
    });
  };


  const FormInput = ({ label, value, onChange, }) => (
    <div className="flex justify-between items-center mb-3">
      <label className="text-sm">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border border-gray-300 rounded px-3 py-2 w-32 sm:w-56 text-right"


      />
    </div>
  );

  const FormSection = ({ title, children }) => (
    <div className="bg-blue-50 p-4 rounded-lg mb-6">
      <h2 className="font-bold mb-3">{title}</h2>
      {children}
    </div>
  );

  const taxItems = [];

  const RegimeSection = ({ title, items, taxableIncome, tax }) => {
    // Calculate rebate under Section 87A based on the title (Old/New Regime)
    let rebate87A = 0;

    const itax = calculatedValues.itax; // Get the total I.Tax from calculated values

    // Determine rebate under Section 87A
    if (title === "Old Regime") {
      if (taxableIncome <= 500000) {
        rebate87A = Math.min(tax, 12500); // Full rebate if tax <= 12500
      } else {
        rebate87A = 0; // No rebate for income above ₹5,00,000
      }
    } else if (title === "New Regime") {
      if (taxableIncome <= 700000) {
        rebate87A = Math.min(tax, 25000); // Full rebate if tax <= 25000
      } else {
        rebate87A = 0; // No rebate for income above ₹7,00,000
      }
    }

    // Calculate Education Cess @ 4%
    let educationCess = 0;
    if (title === "Old Regime" && tax >= 12500) {
      educationCess = tax * 0.04; // 4% of the tax if tax >= 12500
    } else if (title === "New Regime" && tax >= 25000) {
      educationCess = tax * 0.04; // 4% of the tax if tax >= 25000
    }

    // Calculate Total Income Tax PAYABLE
    let totalTaxPayable = tax + educationCess; // Tax + Education Cess

    // Tax Deducted From SALARY is equal to the calculated tax for the respective regime
    const taxDeductedFromSalary = itax; // Use I.Tax total

    // Calculate Net Income Tax to Pay
    const netIncomeTaxToPay = totalTaxPayable - taxDeductedFromSalary; // Total Income Tax PAYABLE - Tax Deducted From SALARY

    return (
      <div className="w-full md:w-1/2 pr-6 mb-6 md:mb-0">
        <h2 className="font-bold text-lg mb-4">{title}</h2>

        {/* Render FormInput for Taxable Income and Tax */}
        <FormInput
          label={`${title} Taxable Income`}
          value={Math.round(taxableIncome * 100) / 100}
          readOnly
        />
        <FormInput
          label={`${title} Tax`}
          value={Math.round(tax * 100) / 100}
          readOnly
        />

        {/* Render FormInput for Rebate Under Section 87A */}
        <FormInput
          label="Rebate Under Section 87A"
          value={Math.round(rebate87A * 100) / 100}
          readOnly
        />

        {/* Render FormInput for Education Cess */}
        <FormInput
          label="Education Cess @ 4%"
          value={Math.round(educationCess * 100) / 100}
          readOnly
        />

        {/* Render FormInput for Total Income Tax PAYABLE */}
        <FormInput
          label="Total Income Tax PAYABLE Rs."
          value={Math.round(totalTaxPayable * 100) / 100}
          readOnly
        />

        {/* Render FormInput for Tax Deducted From SALARY */}
        <FormInput
          label="Tax Deducted From SALARY Rs."
          value={Math.round(taxDeductedFromSalary * 100) / 100} // Updated to use I.Tax total
          readOnly
        />

        {/* Render FormInput for Net Income Tax to Pay */}
        <FormInput
          label="Net Income Tax to Pay Rs."
          value={Math.round(netIncomeTaxToPay * 100) / 100} // Updated calculation
          readOnly
        />

        {/* Render other items */}
        {items.map((item, index) => (
          <FormInput key={index} label={item} />
        ))}
      </div>
    );
  };

  const calculateOldRegimeTax = (taxableIncome) => {
    let tax = 0;
    if (taxableIncome <= 250000) {
      tax = 0;
    } else if (taxableIncome <= 500000) {
      tax = (taxableIncome - 250000) * 0.05;
    } else if (taxableIncome <= 1000000) {
      tax = 250000 * 0.05 + (taxableIncome - 500000) * 0.2;
    } else {
      tax = 250000 * 0.05 + 500000 * 0.2 + (taxableIncome - 1000000) * 0.3;
    }
    return tax;
  };

  const calculateNewRegimeTax = (taxableIncome) => {
    let tax = 0;
    if (taxableIncome <= 250000) {
      tax = 0;
    } else if (taxableIncome <= 500000) {
      tax = (taxableIncome - 250000) * 0.05;
    } else if (taxableIncome <= 750000) {
      tax = 250000 * 0.05 + (taxableIncome - 500000) * 0.1;
    } else if (taxableIncome <= 1000000) {
      tax = 250000 * 0.05 + 250000 * 0.1 + (taxableIncome - 750000) * 0.15;
    } else if (taxableIncome <= 1250000) {
      tax =
        250000 * 0.05 +
        250000 * 0.1 +
        250000 * 0.15 +
        (taxableIncome - 1000000) * 0.2;
    } else if (taxableIncome <= 1500000) {
      tax =
        250000 * 0.05 +
        250000 * 0.1 +
        250000 * 0.15 +
        250000 * 0.2 +
        (taxableIncome - 1250000) * 0.25;
    } else {
      tax =
        250000 * 0.05 +
        250000 * 0.1 +
        250000 * 0.15 +
        250000 * 0.2 +
        250000 * 0.25 +
        (taxableIncome - 1500000) * 0.3;
    }
    return tax;
  };

  const calculateTaxableIncomeOldRegime = (
    grossSalary,
    totalDeduction80C,
    totalDeductionVIA
  ) => {
    const deductionLimit80C = Math.min(totalDeduction80C, 150000);
    return grossSalary - (deductionLimit80C + totalDeductionVIA);
  };

  const calculateTaxableIncomeNewRegime = (grossSalary, standardDeduction) => {
    return grossSalary - standardDeduction;
  };

  useEffect(() => {
    const grossSalary = calculatedValues.grossSalary;
    const totalDeduction80C = calculatedValues.totalDeduction80C;
    const totalDeductionVIA = calculatedValues.totalDeductionVIA;
    const standardDeduction = calculatedValues.standardDeduction;

    const taxableIncomeOldRegime = calculateTaxableIncomeOldRegime(
      grossSalary,
      totalDeduction80C,
      totalDeductionVIA
    );
    const taxableIncomeNewRegime = calculateTaxableIncomeNewRegime(
      grossSalary,
      standardDeduction
    );

    const oldRegimeTax = calculateOldRegimeTax(taxableIncomeOldRegime);
    const newRegimeTax = calculateNewRegimeTax(taxableIncomeNewRegime);

    setCalculatedValues((prev) => ({
      ...prev,
      oldRegimeTaxableIncome: taxableIncomeOldRegime,
      oldRegimeTax,
      newRegimeTaxableIncome: taxableIncomeNewRegime,
      newRegimeTax,
    }));
  }, [
    calculatedValues.grossSalary,
    calculatedValues.totalDeduction80C,
    calculatedValues.totalDeductionVIA,
    calculatedValues.standardDeduction,
  ]);

  return (
    <div className="container py-3">
      {/* Center form */}
      <div className="relative">
        <button
          onClick={() => setDropdownOpen((prev) => !prev)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg focus:outline-none flex items-center justify-between"
        >
          User Admin
          {dropdownOpen ? <ChevronUp className="ml-2" size={20} /> : <ChevronDown className="ml-2" size={20} />}
        </button>

        {dropdownOpen && (
          <div className="absolute left-0 mt-2 w-full sm:w-56 bg-white rounded-md shadow-lg py-2 z-20">

            {/* Income Tax Dropdown */}
            <div className="relative">
              <button
                onClick={() => toggleSubDropdown(0)} // Use unique index
                className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-200 flex justify-between items-center"
              >
                Income Tax
                {subDropdownOpen === 0 ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
              {subDropdownOpen === 0 && (
                <div className="absolute left-full top-0 mt-0 w-48 bg-white rounded-md shadow-lg py-2 z-30">
                  <Link to="/dashboard/user_incometax" className="block px-4 py-2 text-gray-700 hover:bg-gray-200">
                    New
                  </Link>
                  <Link to="/more2" className="block px-4 py-2 text-gray-700 hover:bg-gray-200">
                    Completed Work
                  </Link>
                </div>
              )}
            </div>

            {/* Pension Proposal Dropdown */}
            <div className="relative">
              <button
                onClick={() => toggleSubDropdown(1)} // Use unique index
                className="flex w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-200 justify-between items-center"
              >
                Pension Proposal
                {subDropdownOpen === 1 ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
              {subDropdownOpen === 1 && (
                <div className="absolute left-full top-0 mt-0 w-48 bg-white rounded-md shadow-lg py-2 z-30">
                  <Link to="/dashboard/profile" className="block px-4 py-2 text-gray-700 hover:bg-gray-200">
                    New
                  </Link>
                  <Link to="/dashboard/account" className="block px-4 py-2 text-gray-700 hover:bg-gray-200">
                    Completed Work
                  </Link>
                </div>
              )}
            </div>

            {/* GPF (Final) Dropdown */}
            <div className="relative">
              <button
                onClick={() => toggleSubDropdown(2)} // Use unique index
                className="flex w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-200 justify-between items-center"
              >
                GPF (Final)
                {subDropdownOpen === 2 ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
              {subDropdownOpen === 2 && (
                <div className="absolute left-full top-0 mt-0 w-48 bg-white rounded-md shadow-lg py-2 z-30">
                  <Link to="/dashboard/profile" className="block px-4 py-2 text-gray-700 hover:bg-gray-200">
                    New
                  </Link>
                  <Link to="/dashboard/account" className="block px-4 py-2 text-gray-700 hover:bg-gray-200">
                    Completed Work
                  </Link>
                </div>
              )}
            </div>

            {/* GIS Proposal Dropdown */}
            <div className="relative">
              <button
                onClick={() => toggleSubDropdown(3)} // Use unique index
                className="flex w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-200 justify-between items-center"
              >
                GIS Proposal
                {subDropdownOpen === 3 ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
              {subDropdownOpen === 3 && (
                <div className="absolute left-full top-0 mt-0 w-48 bg-white rounded-md shadow-lg py-2 z-30">
                  <Link to="/dashboard/profile" className="block px-4 py-2 text-gray-700 hover:bg-gray-200">
                    New
                  </Link>
                  <Link to="/dashboard/account" className="block px-4 py-2 text-gray-700 hover:bg-gray-200">
                    Completed Work
                  </Link>
                </div>
              )}
            </div>

            {/* Intern District Transfer Dropdown */}
            <div className="relative">
              <button
                onClick={() => toggleSubDropdown(4)} // Use unique index
                className="flex w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-200 justify-between items-center"
              >
                Intern District Transfer
                {subDropdownOpen === 4 ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
              {subDropdownOpen === 4 && (
                <div className="absolute left-full top-0 mt-0 w-48 bg-white rounded-md shadow-lg py-2 z-30">
                  <Link to="/dashboard/profile" className="block px-4 py-2 text-gray-700 hover:bg-gray-200">
                    New
                  </Link>
                  <Link to="/dashboard/account" className="block px-4 py-2 text-gray-700 hover:bg-gray-200">
                    Completed Work
                  </Link>
                </div>
              )}
            </div>

            {/* GPF (Non Refundable) Dropdown */}
            <div className="relative">
              <button
                onClick={() => toggleSubDropdown(5)} // Use unique index
                className="flex w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-200 justify-between items-center"
              >
                GPF (Non Refundable)
                {subDropdownOpen === 5 ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
              {subDropdownOpen === 5 && (
                <div className="absolute left-full top-0 mt-0 w-48 bg-white rounded-md shadow-lg py-2 z-30">
                  <Link to="/dashboard/gpf_non_refundable" className="block px-4 py-2 text-gray-700 hover:bg-gray-200">
                    New
                  </Link>
                  <Link to="/dashboard/account" className="block px-4 py-2 text-gray-700 hover:bg-gray-200">
                    Completed Work
                  </Link>
                </div>
              )}
            </div>

          </div>
        )}
      </div>
      <div className="min-h-screen flex flex-col">


        {/* Main section starts */}
        <div className="bg-gradient-to-r from-green-400 via-teal-400 to-cyan-400 p-6">
          {/* Office Information Section */}
          <div className="flex flex-col md:flex-row flex-wrap gap-4">
            {/* Office Name */}
            <div className="flex flex-col w-full md:flex-1">
              <span className="block text-gray-800 mb-1 font-bold">Office Name</span>
              <span className="px-3 py-2 border bg-slate-200 border-black rounded-md">ABC Corp</span>
            </div>

            {/* Office Address */}
            <div className="flex flex-col w-full md:flex-1">
              <span className="block text-gray-800 mb-1 font-bold">Office Address</span>
              <span className="px-3 py-2 border bg-slate-200 border-black rounded-md">123 Main St, City, State</span>
            </div>

            {/* Officer / DDO Name */}
            <div className="flex flex-col w-full md:flex-1">
              <span className="block text-gray-800 mb-1 font-bold">Officer / DDO Name</span>
              <span className="px-3 py-2 border bg-slate-200 border-black rounded-md">John Smith</span>
            </div>

            {/* Officer / DDO Designation */}
            <div className="flex flex-col w-full md:flex-1">
              <span className="block text-gray-800 mb-1 font-bold">DDO Designation</span>
              <span className="px-3 py-2 border bg-slate-200 border-black rounded-md">Senior Manager</span>
            </div>

            {/* Office TAN No */}
            <div className="flex flex-col w-full md:flex-1">
              <span className="block text-gray-800 mb-1 font-bold">Office TAN No</span>
              <span className="px-3 py-2 border bg-slate-200 border-black rounded-md">ABCDE1234F</span>
            </div>

            {/* Financial Year */}
            <div className="flex flex-col w-full md:flex-1">
              <span className="block text-gray-800 mb-1 font-bold">Financial Year</span>
              <span className="px-3 py-2 border bg-slate-200 border-black rounded-md">2023-2024</span>
            </div>

            {/* Assessment Year */}
            <div className="flex flex-col w-full md:flex-1">
              <span className="block text-gray-800 mb-1 font-bold">Assessment Year</span>
              <span className="px-3 py-2 border bg-slate-200 border-black rounded-md">2023-2024</span>
            </div>
          </div>
          <br />
          {/* Employee Details Section */}
          <div className="flex flex-col md:flex-row flex-wrap gap-4">
            {/* Employee Name */}
            <div className="flex flex-col w-full md:flex-1">
              <span className="block text-gray-800 mb-1 font-bold">Employee Name</span>
              <span className="px-3 py-2 border bg-slate-200 border-black rounded-md">John Doe</span>
            </div>

            {/* Designation */}
            <div className="flex flex-col w-full md:flex-1">
              <span className="block text-gray-800 mb-1 font-bold">Designation</span>
              <span className="px-3 py-2 border bg-slate-200 border-black rounded-md">Software Engineer</span>
            </div>

            {/* PAN No */}
            <div className="flex flex-col w-full md:flex-1">
              <span className="block text-gray-800 mb-1 font-bold">PAN No.</span>
              <span className="px-3 py-2 border bg-slate-200 border-black rounded-md">ABCDE1234F</span>
            </div>

            {/* Aadhar No */}
            <div className="flex flex-col w-full md:flex-1">
              <span className="block text-gray-800 mb-1 font-bold">Aadhar No</span>
              <span className="px-3 py-2 border bg-slate-200 border-black rounded-md">1234 5678 9012</span>
            </div>

            {/* Date of Birth */}
            <div className="flex flex-col w-full md:flex-1">
              <span className="block text-gray-800 mb-1 font-bold">Date of Birth</span>
              <span className="px-3 py-2 border bg-slate-200 border-black rounded-md">1990-01-01</span>
            </div>

            {/* Pay Scale */}
            <div className="flex flex-col w-full md:flex-1">
              <span className="block text-gray-800 mb-1 font-bold">Pay Scale</span>
              <span className="px-3 py-2 border bg-slate-200 border-black rounded-md">₹50,000 - ₹70,000</span>
            </div>

            {/* Cadre */}
            <div className="flex flex-col w-full md:flex-1">
              <span className="block text-gray-800 mb-1 font-bold">Cadre</span>
              <span className="px-3 py-2 border bg-slate-200 border-black rounded-md">General</span>
            </div>

            {/* Gender */}
            <div className="flex flex-col w-full md:flex-1">
              <span className="block text-gray-800 mb-1 font-bold">Gender</span>
              <span className="px-3 py-2 border bg-slate-200 border-black rounded-md">Male</span>
            </div>
          </div>
        </div>
        <main className="flex-grow bg-gray-50 py-6">
          <div className="container mx-auto px-4">
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="flex flex-col md:flex-row md:w-auto md:ml-auto mt-4 md:mt-0 items-center">
                <div className="flex flex-col items-center mb-6 w-full text-center">
                  <h1 className="text-2xl md:text-3xl font-semibold text-gray-800">
                    Income Tax Calculation
                  </h1>
                  <h2 className="text-xl text-red-500 font-medium mt-2">
                    Monthly Statement showing Salary & Deduction Details
                  </h2>
                </div>

                <div className="flex items-center space-x-4">
                  <label className="text-gray-700 font-medium">GPF:</label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="gpf"
                      value="yes"
                      className="form-radio text-indigo-600"
                      checked={gpfValue === "yes"}
                      onChange={handleGpfChange}
                    />
                    <span className="ml-2">Yes</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="gpf"
                      value="no"
                      className="form-radio text-indigo-600"
                      checked={gpfValue === "no"}
                      onChange={handleGpfChange}
                    />
                    <span className="ml-2">No</span>
                  </label>
                </div>
              </div>


              <div className="overflow-x-auto">
                <div className="min-w-[1000px]"> {/* Set a smaller minimum width for better responsiveness */}
                  <table className="w-full border-collapse">
                    <thead>
                      <tr>
                        <th className="sticky left-0 z-10 border border-gray-300 bg-gray-100 p-2 text-left">
                          Month
                        </th>
                        {columns.map((column, index) => (
                          <th
                            key={index}
                            className="border border-gray-300 bg-gray-100 p-2 text-left"
                          >
                            {column}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {months.map((month, rowIndex) => (
                        <tr
                          key={rowIndex}
                          className={rowIndex % 2 === 0 ? "bg-white" : "bg-gray-50"}
                        >
                          <td className="sticky left-0 z-10 border border-gray-300 p-2 text-blue-500 bg-inherit">
                            <label className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={selectedMonths[month]}
                                onChange={() => handleMonthSelect(month)}
                                className="form-checkbox"
                              />
                              <span className="truncate">{month}</span> {/* Added truncate for long month names */}
                            </label>
                          </td>
                          {columns.map((column, colIndex) => (
                            <td
                              key={colIndex}
                              className="border border-gray-300 p-2"
                            >
                              <input
                                type="text"
                                className="w-full bg-transparent outline-none text-center focus:ring-2 focus:ring-blue-300 rounded"
                                value={monthValues[month]?.[column] !== undefined ? monthValues[month][column] : "0"}
                                onChange={(e) =>
                                  handleInputChange(month, column, e.target.value)
                                }
                                disabled={(gpfValue === "yes" && (column === "NPS" || column === "DCPS")) || (gpfValue === "no" && column === "GPF")}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                      <tr className="font-semibold bg-gray-100">
                        <td className="sticky left-0 z-10 border border-gray-300 p-2 text-red-500 bg-gray-100">
                          Total
                        </td>
                        {calculateTotals().map((total, index) => (
                          <td
                            key={index}
                            className="border border-gray-300 p-2 text-red-500 text-center"
                          >
                            {Math.round(total)} {/* Rounding the total to the nearest whole number */}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>


              <div className="mt-6 flex justify-end space-x-4">
                <button className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors">
                  Calculate
                </button>
                <button className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors">
                  Reset
                </button>
              </div>
            </div>
          </div>
        </main>
        {/* Main section ends */}

        {/* Sub Section starts */}

        <div className="max-w-8xl mx-auto p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormSection title="Income">
              <FormInput
                label="Gross Salary"
                value={Math.round(calculatedValues.grossSalary * 100) / 100}
                readOnly
              />
              <FormInput
                label="Other Income"
                value={Math.round(calculatedValues.otherIncome * 100) / 100}
                onChange={(value) =>
                  handleCalculatedValueChange("otherIncome", value)
                }
              />
              <FormInput
                label="Total Income"
                value={Math.round(calculatedValues.totalIncome * 100) / 100}
                readOnly
              />
              <div className="mt-4">
                <h3 className="font-semibold mb-2">Deduction</h3>
                <FormInput
                  label="Standard Deduction"
                  value={
                    Math.round(calculatedValues.standardDeduction * 100) / 100
                  }
                  readOnly
                />
                <FormInput
                  label="P.TAX"
                  value={Math.round(calculatedValues.ptax * 100) / 100}
                  readOnly
                />
              </div>
              <div className="mt-4">
                <h3 className="font-semibold mb-2">Home Loan</h3>
                <FormInput
                  label="Home Loan Interest"
                  value={
                    Math.round(calculatedValues.homeLoanInterest * 100) / 100
                  }
                  onChange={(value) =>
                    handleCalculatedValueChange("homeLoanInterest", value)
                  }
                />
              </div>
              <div className="mt-4">
                <h3 className="font-semibold mb-2">HRA Rent</h3>
                <FormInput
                  label="HRA Exemption"
                  value={Math.round(calculatedValues.hraExemption * 100) / 100}
                  onChange={(value) =>
                    handleCalculatedValueChange("hraExemption", value)
                  }
                />
              </div>
            </FormSection>

            <FormSection title="Deduction U/S 80-C">
              <FormInput
                label="GPF"
                value={Math.round(calculatedValues.gpf * 100) / 100}
                readOnly
              />
              <FormInput
                label="DCPS"
                value={Math.round(calculatedValues.dcps * 100) / 100}
                readOnly
              />
              <FormInput
                label="GIS"
                value={Math.round(calculatedValues.gis * 100) / 100}
                readOnly
              />
              <FormInput
                label="LIC"
                value={Math.round(calculatedValues.lic * 100) / 100}
                readOnly
              />
              <FormInput
                label="Tution Fees"
                value={Math.round(calculatedValues.tutionFees * 100) / 100}
                onChange={(value) =>
                  handleCalculatedValueChange("tutionFees", value)
                }
              />
              <FormInput
                label="NSC"
                value={Math.round(calculatedValues.nsc * 100) / 100}
                onChange={(value) => handleCalculatedValueChange("nsc", value)}
              />
              <FormInput
                label="PLI/RD"
                value={Math.round(calculatedValues.pliRd * 100) / 100}
                onChange={(value) => handleCalculatedValueChange("pliRd", value)}
              />
              <FormInput
                label="Home Loan Principal"
                value={Math.round(calculatedValues.homeLoanPrincipal * 100) / 100}
                onChange={(value) =>
                  handleCalculatedValueChange("homeLoanPrincipal", value)
                }
              />
              <FormInput
                label="Mutual Fund"
                value={Math.round(calculatedValues.mutualFund * 100) / 100}
                onChange={(value) =>
                  handleCalculatedValueChange("mutualFund", value)
                }
              />
              <FormInput
                label="Total Deduction U/S 80-C"
                value={Math.round(calculatedValues.totalDeduction80C * 100) / 100}
                readOnly
              />
            </FormSection>

            <FormSection title="Deduction U/S VI-A" >
              <FormInput
                label="CCD 1B Additional Deducton to NPS"
                value={Math.round(calculatedValues.ccd1b * 100) / 100}
                onChange={(value) => handleCalculatedValueChange("ccd1b", value)}
              />
              
              <FormInput
                label="CCD 2 Deduction contribution (NPS)"
                value={Math.round(calculatedValues.ccd2 * 100) / 100}
                onChange={(value) => handleCalculatedValueChange("ccd2", value)}
              />
              <FormInput
                label="U/S 80D Mediclaim Insurance"
                value={Math.round(calculatedValues.mediclaim * 100) / 100}
                onChange={(value) =>
                  handleCalculatedValueChange("mediclaim", value)
                }
              />
              <FormInput
                label="U/S DD Dependent Handicapped P"
                value={
                  Math.round(calculatedValues.dependentHandicapped * 100) / 100
                }
                onChange={(value) =>
                  handleCalculatedValueChange("dependentHandicapped", value)
                }
              />
              <FormInput
                label="U/S 80E Interest on educational loan"
                value={
                  Math.round(calculatedValues.educationLoanInterest * 100) / 100
                }
                onChange={(value) =>
                  handleCalculatedValueChange("educationLoanInterest", value)
                }
              />
              <FormInput
                label="U/S 80EEB Electric Vehicle Loan"
                value={
                  Math.round(calculatedValues.electricVehicleLoan * 100) / 100
                }
                onChange={(value) =>
                  handleCalculatedValueChange("electricVehicleLoan", value)
                }
              />
              <FormInput
                label="U/S 80 G Donation"
                value={Math.round(calculatedValues.donation * 100) / 100}
                onChange={(value) =>
                  handleCalculatedValueChange("donation", value)
                }
              />
              <FormInput
                label="U/S 80 U Self Handicapped Person"
                value={Math.round(calculatedValues.selfHandicapped * 100) / 100}
                onChange={(value) =>
                  handleCalculatedValueChange("selfHandicapped", value)
                }
              />
              <FormInput
                label="Total Deduction U/S VI-A"
                value={Math.round(calculatedValues.totalDeductionVIA * 100) / 100}
                readOnly
              />
            </FormSection>
          </div>

          <div className="flex flex-col lg:flex-row justify-between items-center px-4 lg:px-28 bg-slate-100 p-4 space-y-4 lg:space-y-0 lg:space-x-4">
            {/* Left section with Regime info */}
            <div className="flex flex-col lg:flex-row space-y-4 lg:space-y-0 lg:space-x-8">
              <RegimeSection
                title="Old Regime"
                items={taxItems}
                taxableIncome={calculatedValues.oldRegimeTaxableIncome}
                tax={calculatedValues.oldRegimeTax}
              />

              <RegimeSection
                title="New Regime"
                items={taxItems}
                taxableIncome={calculatedValues.newRegimeTaxableIncome}
                tax={calculatedValues.newRegimeTax}
              />
            </div>

            {/* Right section with action buttons */}
            <div className="flex flex-col lg:flex-row space-y-2 lg:space-y-0 lg:space-x-4">
              <button className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">Save</button>
              <button className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600">Edit</button>
              <button className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600">Update</button>
              <button className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600">Print</button>
            </div>
          </div>





        </div>
      </div>


      {/* Sub Section Ends */}
    </div>
  );
};

export default IncometaxTable;